import React, { useState, useEffect, useCallback } from 'react';
import universalDataService from '../services/universalDataService';

const TIPURI = ['Arat', 'Discuit', 'Semănat', 'Fertilizare', 'Erbicidare', 'Tratament', 'Recoltat', 'Irigat', 'Altele'];
const CULORI_TIP = {
  'Arat': '#92400e', 'Discuit': '#78350f', 'Semănat': '#166534', 'Fertilizare': '#1e40af',
  'Erbicidare': '#6d28d9', 'Tratament': '#be123c', 'Recoltat': '#d97706',
  'Irigat': '#0891b2', 'Altele': '#4b5563'
};

const getAvgMotorinPrice = (motorina) => {
  if (!motorina?.purchases?.length) return 0;
  const totalLiters = motorina.purchases.reduce((s, p) => s + (p.liters || 0), 0);
  const totalCost = motorina.purchases.reduce((s, p) => s + ((p.liters || 0) * (p.pricePerLiter || 0)), 0);
  return totalLiters > 0 ? totalCost / totalLiters : 0;
};

const emptyMaterial = () => ({ id: Date.now() + Math.random(), productId: '', productName: '', doza: '', dozaUnit: 'kg/ha', unitPrice: '', totalQty: 0, totalCost: 0 });
const emptyForm = () => ({
  date: new Date().toISOString().split('T')[0],
  type: 'Arat',
  parcele: [],
  materiale: [],
  echipamentId: '',
  echipamentName: '',
  motorina: { litersPerHa: '', pricePerLiter: 0, totalLiters: 0, totalCost: 0 },
  notes: ''
});

const Lucrari = () => {
  const [lucrari, setLucrari] = useState([]);
  const [fields, setFields] = useState([]);
  const [produse, setProduse] = useState([]);
  const [utilaje, setUtilaje] = useState([]);
  const [avgMotorinPrice, setAvgMotorinPrice] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterType, setFilterType] = useState('');
  const [loading, setLoading] = useState(true);

  // ── Load all data ─────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [lData, fData, pData, uData, mData] = await Promise.all([
        universalDataService.loadLucrari(),
        universalDataService.loadFields().catch(() => []),
        universalDataService.loadProduse(),
        universalDataService.loadUtilaje(),
        universalDataService.loadMotorina()
      ]);
      setLucrari(Array.isArray(lData) ? lData : []);
      setFields(Array.isArray(fData) ? fData : []);
      setProduse(Array.isArray(pData) ? pData : []);
      setUtilaje(Array.isArray(uData) ? uData : []);
      setAvgMotorinPrice(getAvgMotorinPrice(mData));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Compute totals in form ────────────────────────────────────────────────
  const totalHa = form.parcele.reduce((s, p) => s + (parseFloat(p.ha) || 0), 0);

  const recalcMaterial = (mat, ha) => {
    const doza = parseFloat(mat.doza) || 0;
    const price = parseFloat(mat.unitPrice) || 0;
    const isPerHa = mat.dozaUnit.includes('/ha');
    const totalQty = isPerHa ? doza * ha : doza;
    return { ...mat, totalQty: +totalQty.toFixed(3), totalCost: +(totalQty * price).toFixed(2) };
  };

  const recalcMotorinaInForm = (litersPerHa, price, ha) => {
    const lph = parseFloat(litersPerHa) || 0;
    const p = parseFloat(price) || 0;
    const total = lph * ha;
    return { litersPerHa, pricePerLiter: p, totalLiters: +total.toFixed(1), totalCost: +(total * p).toFixed(2) };
  };

  // ── Form handlers ─────────────────────────────────────────────────────────
  const handleFieldToggle = (field) => {
    setForm(prev => {
      const exists = prev.parcele.find(p => p.fieldId === field.id);
      const newParcele = exists
        ? prev.parcele.filter(p => p.fieldId !== field.id)
        : [...prev.parcele, { fieldId: field.id, fieldName: field.name, ha: parseFloat(field.area) || 0 }];
      const newHa = newParcele.reduce((s, p) => s + (parseFloat(p.ha) || 0), 0);
      return {
        ...prev,
        parcele: newParcele,
        materiale: prev.materiale.map(m => recalcMaterial(m, newHa)),
        motorina: recalcMotorinaInForm(prev.motorina.litersPerHa, prev.motorina.pricePerLiter, newHa)
      };
    });
  };

  const handleMaterialChange = (idx, key, value) => {
    setForm(prev => {
      const mats = [...prev.materiale];
      let mat = { ...mats[idx], [key]: value };
      if (key === 'productId') {
        const prod = produse.find(p => p.id === value);
        if (prod) { mat.productName = prod.name; mat.unitPrice = prod.lastPrice || ''; }
      }
      mats[idx] = recalcMaterial(mat, totalHa);
      return { ...prev, materiale: mats };
    });
  };

  const addMaterial = () => setForm(prev => ({ ...prev, materiale: [...prev.materiale, emptyMaterial()] }));
  const removeMaterial = (idx) => setForm(prev => ({ ...prev, materiale: prev.materiale.filter((_, i) => i !== idx) }));

  const handleMotorinChange = (litersPerHa) => {
    setForm(prev => ({
      ...prev,
      motorina: recalcMotorinaInForm(litersPerHa, prev.motorina.pricePerLiter || avgMotorinPrice, totalHa)
    }));
  };

  const handleEquipmentChange = (id) => {
    const eq = utilaje.find(e => e.id === id);
    setForm(prev => ({ ...prev, echipamentId: id, echipamentName: eq ? eq.name : '' }));
  };

  const openAdd = () => {
    setForm({ ...emptyForm(), motorina: { litersPerHa: '', pricePerLiter: avgMotorinPrice, totalLiters: 0, totalCost: 0 } });
    setEditId(null);
    setShowForm(true);
  };

  const openEdit = (l) => {
    setForm({ ...l, motorina: l.motorina || { litersPerHa: '', pricePerLiter: avgMotorinPrice, totalLiters: 0, totalCost: 0 } });
    setEditId(l.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.parcele.length) return alert('Selectați cel puțin un câmp.');
    const ha = totalHa;
    const matCost = form.materiale.reduce((s, m) => s + (m.totalCost || 0), 0);
    const motCost = form.motorina.totalCost || 0;
    const record = {
      ...form,
      id: editId || Date.now(),
      year: parseInt(form.date.split('-')[0]),
      totalHa: +ha.toFixed(4),
      totalCost: +(matCost + motCost).toFixed(2)
    };
    let updated;
    if (editId) {
      updated = lucrari.map(l => l.id === editId ? record : l);
    } else {
      updated = [...lucrari, record];
    }
    await universalDataService.saveLucrari(updated);
    setLucrari(updated);
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ștergeți această lucrare?')) return;
    const updated = lucrari.filter(l => l.id !== id);
    await universalDataService.saveLucrari(updated);
    setLucrari(updated);
  };

  // ── Filtered list ─────────────────────────────────────────────────────────
  const years = [...new Set(lucrari.map(l => l.year))].sort((a, b) => b - a);
  const filtered = lucrari.filter(l =>
    (!filterYear || l.year === filterYear) &&
    (!filterType || l.type === filterType)
  ).sort((a, b) => new Date(b.date) - new Date(a.date));

  const yearSummary = filtered.reduce((s, l) => ({
    ha: s.ha + l.totalHa,
    cost: s.cost + l.totalCost
  }), { ha: 0, cost: 0 });

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={S.page}>
      <div style={S.header}><h2 style={S.title}>🚜 Lucrări</h2></div>
      <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}>Se încarcă…</div>
    </div>
  );

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>🚜 Lucrări Agricole</h2>
          <span style={S.sub}>{lucrari.length} lucrări înregistrate</span>
        </div>
        <button onClick={openAdd} style={S.addBtn}>+ Adaugă Lucrare</button>
      </div>

      {/* Filters */}
      <div style={S.filters}>
        <select value={filterYear} onChange={e => setFilterYear(e.target.value ? +e.target.value : '')} style={S.select}>
          <option value="">Toți anii</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
          {!years.includes(new Date().getFullYear()) && <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={S.select}>
          <option value="">Toate tipurile</option>
          {TIPURI.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {filtered.length > 0 && (
          <div style={S.summary}>
            <span>{filtered.length} lucrări</span>
            <span style={{ margin: '0 8px' }}>·</span>
            <span>{yearSummary.ha.toFixed(1)} ha total</span>
            <span style={{ margin: '0 8px' }}>·</span>
            <span style={{ fontWeight: 700, color: '#dc2626' }}>{yearSummary.cost.toFixed(0)} lei total</span>
          </div>
        )}
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div style={S.empty}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🚜</div>
          <div style={{ fontWeight: 600 }}>Nicio lucrare înregistrată</div>
          <div style={{ color: '#9ca3af', marginTop: 4 }}>Apăsați „+ Adaugă Lucrare" pentru a începe</div>
        </div>
      ) : (
        <div style={S.list}>
          {filtered.map(l => (
            <div key={l.id} style={S.card}>
              <div style={S.cardLeft}>
                <span style={{ ...S.typeBadge, background: CULORI_TIP[l.type] || '#4b5563' }}>{l.type}</span>
                <div style={S.cardDate}>{new Date(l.date).toLocaleDateString('ro-RO')}</div>
              </div>
              <div style={S.cardMid}>
                <div style={S.cardFields}>
                  {l.parcele.map(p => <span key={p.fieldId} style={S.fieldChip}>{p.fieldName} ({p.ha} ha)</span>)}
                </div>
                <div style={S.cardStats}>
                  <span>📐 {l.totalHa.toFixed(2)} ha</span>
                  {l.materiale.length > 0 && <span>📦 {l.materiale.length} materiale</span>}
                  {l.motorina?.totalLiters > 0 && <span>⛽ {l.motorina.totalLiters.toFixed(0)} L</span>}
                  {l.echipamentName && <span>🚜 {l.echipamentName}</span>}
                </div>
                {l.notes && <div style={S.cardNote}>📝 {l.notes}</div>}
              </div>
              <div style={S.cardRight}>
                <div style={S.cardCost}>{l.totalCost.toFixed(0)} lei</div>
                {l.totalHa > 0 && <div style={S.cardCostHa}>{(l.totalCost / l.totalHa).toFixed(0)} lei/ha</div>}
                <div style={S.cardActions}>
                  <button onClick={() => openEdit(l)} style={S.editBtn}>✏️</button>
                  <button onClick={() => handleDelete(l.id)} style={S.deleteBtn}>🗑️</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div style={S.modal}>
            <div style={S.modalHeader}>
              <h3 style={{ margin: 0, fontSize: 18 }}>{editId ? 'Editează Lucrare' : 'Adaugă Lucrare'}</h3>
              <button onClick={() => setShowForm(false)} style={S.closeBtn}>✕</button>
            </div>

            <div style={S.modalBody}>
              {/* Row 1: Date + Type */}
              <div style={S.row}>
                <div style={S.field}>
                  <label style={S.label}>Data *</label>
                  <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} style={S.input} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Tip lucrare *</label>
                  <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} style={S.input}>
                    {TIPURI.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={S.field}>
                  <label style={S.label}>Utilaj</label>
                  <select value={form.echipamentId} onChange={e => handleEquipmentChange(e.target.value)} style={S.input}>
                    <option value="">Fără utilaj</option>
                    {utilaje.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Fields selection */}
              <div style={{ marginBottom: 16 }}>
                <label style={S.label}>Câmpuri lucrare * {totalHa > 0 && <span style={{ color: '#166534', fontWeight: 700 }}>(Total: {totalHa.toFixed(2)} ha)</span>}</label>
                <div style={S.fieldGrid}>
                  {fields.length === 0 ? (
                    <div style={{ color: '#9ca3af', padding: 8 }}>Niciun câmp disponibil. Adăugați câmpuri în secțiunea Câmpuri.</div>
                  ) : fields.map(f => {
                    const sel = form.parcele.find(p => p.fieldId === f.id);
                    return (
                      <div key={f.id} onClick={() => handleFieldToggle(f)}
                        style={{ ...S.fieldChipSel, background: sel ? '#dcfce7' : '#f9fafb', borderColor: sel ? '#16a34a' : '#d1d5db', cursor: 'pointer' }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{f.name}</span>
                        <span style={{ fontSize: 11, color: '#6b7280' }}>{f.area} ha{f.crop ? ` · ${f.crop}` : ''}</span>
                        {sel && <span style={{ color: '#16a34a', fontSize: 11, fontWeight: 700 }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Materiale */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={S.label}>Materiale utilizate</label>
                  <button onClick={addMaterial} style={S.smallBtn}>+ Material</button>
                </div>
                {form.materiale.length === 0 && (
                  <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 8 }}>Niciun material adăugat</div>
                )}
                {form.materiale.map((mat, idx) => (
                  <div key={mat.id} style={S.matRow}>
                    <select value={mat.productId} onChange={e => handleMaterialChange(idx, 'productId', e.target.value)} style={{ ...S.input, flex: 2 }}>
                      <option value="">Selectează produs</option>
                      {produse.map(p => <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>)}
                    </select>
                    <input type="number" placeholder="Doză" value={mat.doza}
                      onChange={e => handleMaterialChange(idx, 'doza', e.target.value)} style={{ ...S.input, width: 80 }} />
                    <select value={mat.dozaUnit} onChange={e => handleMaterialChange(idx, 'dozaUnit', e.target.value)} style={{ ...S.input, width: 90 }}>
                      <option value="kg/ha">kg/ha</option>
                      <option value="L/ha">L/ha</option>
                      <option value="kg">kg total</option>
                      <option value="L">L total</option>
                    </select>
                    <input type="number" placeholder="Lei/unitate" value={mat.unitPrice}
                      onChange={e => handleMaterialChange(idx, 'unitPrice', e.target.value)} style={{ ...S.input, width: 100 }} />
                    <div style={S.matCalc}>
                      <div style={{ fontWeight: 700 }}>{mat.totalCost.toFixed(0)} lei</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{mat.totalQty.toFixed(1)} {mat.dozaUnit.replace('/ha', '')}</div>
                    </div>
                    <button onClick={() => removeMaterial(idx)} style={S.deleteBtn}>✕</button>
                  </div>
                ))}
              </div>

              {/* Motorina */}
              <div style={{ marginBottom: 16 }}>
                <label style={S.label}>Consum motorină</label>
                <div style={S.row}>
                  <div style={S.field}>
                    <label style={{ ...S.label, fontSize: 11 }}>Litri/ha</label>
                    <input type="number" value={form.motorina.litersPerHa}
                      onChange={e => handleMotorinChange(e.target.value)} style={S.input} placeholder="0" />
                  </div>
                  <div style={S.field}>
                    <label style={{ ...S.label, fontSize: 11 }}>Preț/litru (lei)</label>
                    <input type="number" value={form.motorina.pricePerLiter}
                      onChange={e => setForm(p => ({ ...p, motorina: recalcMotorinaInForm(p.motorina.litersPerHa, e.target.value, totalHa) }))}
                      style={S.input} placeholder={avgMotorinPrice.toFixed(2)} />
                  </div>
                  <div style={S.field}>
                    <label style={{ ...S.label, fontSize: 11 }}>Total</label>
                    <div style={{ ...S.input, background: '#f9fafb', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <span style={{ fontWeight: 700 }}>{form.motorina.totalCost.toFixed(0)} lei</span>
                      <span style={{ fontSize: 11, color: '#6b7280' }}>{form.motorina.totalLiters.toFixed(1)} L</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div style={{ marginBottom: 16 }}>
                <label style={S.label}>Note</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  style={{ ...S.input, height: 60, resize: 'vertical' }} placeholder="Observații, doze, condiții…" />
              </div>

              {/* Total */}
              <div style={S.totalRow}>
                <span>Total costuri lucrare:</span>
                <span style={{ fontWeight: 800, fontSize: 20, color: '#dc2626' }}>
                  {(form.materiale.reduce((s, m) => s + (m.totalCost || 0), 0) + (form.motorina.totalCost || 0)).toFixed(0)} lei
                </span>
                {totalHa > 0 && (
                  <span style={{ fontSize: 13, color: '#6b7280' }}>
                    ({((form.materiale.reduce((s, m) => s + (m.totalCost || 0), 0) + (form.motorina.totalCost || 0)) / totalHa).toFixed(0)} lei/ha)
                  </span>
                )}
              </div>
            </div>

            <div style={S.modalFooter}>
              <button onClick={() => setShowForm(false)} style={S.cancelBtn}>Anulează</button>
              <button onClick={handleSave} style={S.saveBtn}>💾 Salvează Lucrare</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────
const S = {
  page: { padding: 24, maxWidth: 1100, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title: { margin: 0, fontSize: 24, fontWeight: 700, color: '#1f2937' },
  sub: { color: '#6b7280', fontSize: 13 },
  addBtn: { padding: '10px 20px', background: '#166534', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  filters: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' },
  select: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, background: '#fff' },
  summary: { fontSize: 13, color: '#374151', padding: '6px 12px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', display: 'flex', gap: 16, alignItems: 'flex-start', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  cardLeft: { minWidth: 90, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 },
  typeBadge: { color: '#fff', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap' },
  cardDate: { fontSize: 11, color: '#6b7280', textAlign: 'center' },
  cardMid: { flex: 1 },
  cardFields: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 },
  fieldChip: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '2px 8px', fontSize: 12, color: '#166534' },
  cardStats: { display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: '#374151' },
  cardNote: { fontSize: 12, color: '#9ca3af', marginTop: 4, fontStyle: 'italic' },
  cardRight: { minWidth: 100, textAlign: 'right' },
  cardCost: { fontSize: 18, fontWeight: 800, color: '#dc2626' },
  cardCostHa: { fontSize: 12, color: '#6b7280' },
  cardActions: { display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 8 },
  editBtn: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', padding: '4px 8px', fontSize: 14 },
  deleteBtn: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', padding: '4px 8px', fontSize: 14, color: '#dc2626' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#6b7280' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' },
  modal: { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 760, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', marginTop: 20 },
  modalHeader: { padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeBtn: { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' },
  modalBody: { padding: '20px', maxHeight: '70vh', overflowY: 'auto' },
  modalFooter: { padding: '16px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 },
  row: { display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' },
  field: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 120 },
  label: { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 },
  input: { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, width: '100%', boxSizing: 'border-box' },
  fieldGrid: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  fieldChipSel: { display: 'flex', flexDirection: 'column', padding: '6px 10px', borderRadius: 8, border: '1px solid', minWidth: 100, maxWidth: 160, gap: 2 },
  matRow: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' },
  matCalc: { minWidth: 80, textAlign: 'right', fontSize: 13 },
  smallBtn: { padding: '6px 12px', background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  totalRow: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' },
  cancelBtn: { padding: '10px 20px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14 },
  saveBtn: { padding: '10px 24px', background: '#166534', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 },
};

export default Lucrari;
